import { useState, useEffect, useCallback, useRef } from 'react'
import { api } from '../context/AuthContext'
import { GOOGLE_API_KEY } from '../constant/api'

// ── Load Google Maps script once ──────────────────────────────────────────────
function loadGoogleMaps(apiKey) {
  return new Promise((resolve, reject) => {
    if (window.google?.maps?.places) { resolve(); return }
    if (document.getElementById('gm-script')) {
      // already injecting — wait for it
      const check = setInterval(() => {
        if (window.google?.maps?.places) { clearInterval(check); resolve() }
      }, 100)
      return
    }
    const s = document.createElement('script')
    s.id = 'gm-script'
    s.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`
    s.async = true
    s.onload  = resolve
    s.onerror = reject
    document.head.appendChild(s)
  })
}

// ── Extract address components from Google Place ──────────────────────────────
function parsePlace(place) {
  const get = (types) => {
    const c = place.address_components?.find(a => types.some(t => a.types.includes(t)))
    return c?.long_name || ''
  }
  return {
    city    : get(['locality', 'administrative_area_level_1']),
    district: get(['administrative_area_level_2', 'administrative_area_level_3']),
    area    : get(['sublocality_level_1', 'sublocality', 'neighborhood']),
    lat     : place.geometry?.location?.lat() ?? null,
    lng     : place.geometry?.location?.lng() ?? null,
  }
}

// ── Places Autocomplete input component ──────────────────────────────────────
function PlaceAutocomplete({ value, onChange, onPlaceSelect, placeholder = 'Search area…', style: s = {} }) {
  const inputRef = useRef(null)
  const acRef    = useRef(null)

  useEffect(() => {
    let mounted = true
    loadGoogleMaps(GOOGLE_API_KEY).then(() => {
      if (!mounted || !inputRef.current) return
      acRef.current = new window.google.maps.places.Autocomplete(inputRef.current, {
        types: ['geocode', 'establishment'],
        componentRestrictions: { country: 'rw' },
        fields: ['address_components', 'geometry', 'formatted_address', 'name'],
      })
      acRef.current.addListener('place_changed', () => {
        const place = acRef.current.getPlace()
        if (!place.geometry) return
        const parsed = parsePlace(place)
        const displayName = place.name || place.formatted_address || ''
        onChange(displayName)
        onPlaceSelect({ ...parsed, formatted_address: place.formatted_address, zone_name: displayName })
      })
    }).catch(console.error)
    return () => { mounted = false }
  }, [])

  return (
    <input
      ref={inputRef}
      type="text"
      value={value}
      onChange={e => onChange(e.target.value)}
      placeholder={placeholder}
      style={{ ...s }}
      autoComplete="off"
    />
  )
}

// ── Helpers ───────────────────────────────────────────────────────────────────
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-GB', { day:'2-digit', month:'short', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'

// ── Shared styles ─────────────────────────────────────────────────────────────
const input = {
  height: 40, borderRadius: 8, border: '1px solid #e9e9e4', padding: '0 12px',
  fontSize: 13, fontWeight: 500, color: '#111317', outline: 'none',
  background: '#fff', fontFamily: "'Plus Jakarta Sans',sans-serif",
  width: '100%', boxSizing: 'border-box',
}
const label = {
  display: 'block', fontSize: 11, fontWeight: 700, color: '#111317',
  textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 6,
  fontFamily: "'JetBrains Mono',monospace",
}
const hint = {
  fontSize: 11, color: '#9a9a94', marginTop: 4, fontFamily: "'Plus Jakarta Sans',sans-serif",
}

// ── Section wrapper ───────────────────────────────────────────────────────────
function Section({ title, description, icon, children, danger }) {
  return (
    <div style={{ background: '#fff', border: `1px solid ${danger ? 'rgba(239,68,68,0.3)' : '#e9e9e4'}`, borderRadius: 12, overflow: 'hidden', marginBottom: 20 }}>
      <div style={{ padding: '16px 22px', borderBottom: '1px solid #e9e9e4', background: danger ? '#fef9f9' : '#fafaf8', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span style={{ fontSize: 18 }}>{icon}</span>
        <div>
          <div style={{ fontSize: 14, fontWeight: 800, color: danger ? '#b91c1c' : '#111317' }}>{title}</div>
          {description && <div style={{ fontSize: 12, color: '#9a9a94', marginTop: 2 }}>{description}</div>}
        </div>
      </div>
      <div style={{ padding: '20px 22px' }}>{children}</div>
    </div>
  )
}

// ── Grid row ─────────────────────────────────────────────────────────────────
function Grid({ cols = 3, children }) {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 16 }}>
      {children}
    </div>
  )
}

// ── Toggle ────────────────────────────────────────────────────────────────────
function Toggle({ checked, onChange, disabled }) {
  return (
    <div onClick={() => !disabled && onChange(!checked)}
      style={{ width: 44, height: 24, borderRadius: 12, background: checked ? '#e8440a' : '#e9e9e4', cursor: disabled ? 'not-allowed' : 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
      <div style={{ position: 'absolute', top: 3, left: checked ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 3px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
    </div>
  )
}

// ── Category Item ─────────────────────────────────────────────────────────────
function CategoryItem({ cat, onEdit, onDelete }) {
  const attrs = (() => {
    try { return Array.isArray(cat.attribute_defs) ? cat.attribute_defs : JSON.parse(cat.attribute_defs || '[]') } catch { return [] }
  })()
  const hasVariants = attrs.length > 0

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
      {cat.icon && <span style={{ fontSize: 20 }}>{cat.icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 7 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{cat.name}</div>
          {hasVariants && (
            <span style={{ fontSize: 9, fontWeight: 800, background: '#fff3f0', color: '#e8440a', border: '1px solid #fdd0c0', borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              ✦ {attrs.length} VARIANT {attrs.length === 1 ? 'ATTR' : 'ATTRS'}
            </span>
          )}
        </div>
        {hasVariants
          ? <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 1 }}>{attrs.map(a => a.name).join(' · ')}</div>
          : cat.description && <div style={{ fontSize: 11, color: '#9a9a94', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{cat.description}</div>
        }
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 3, background: cat.active ? '#f0fdf4' : '#fef2f2', color: cat.active ? '#15803d' : '#b91c1c' }}>
        {cat.active ? 'Active' : 'Hidden'}
      </div>
      <button onClick={() => onEdit(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Edit</button>
      <button onClick={() => onDelete(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
    </div>
  )
}


// ── Icon Picker ───────────────────────────────────────────────────────────────
const SUGGESTED_ICONS = ['👗','👟','💄','📱','🍕','🥤','⚽','🏠','📚','🎵','🧴','🌿','🧸','💍','🎮','🖥','🧳','🐾','🌸','🪴','🧁','🍳','🔧','🎨','🪑','🏋','🚗','✂️','🧵','💊','🛋','🏺','🪆','🎁','🧶','🥾','🌺','📸','🕯','🫙','🛍','🧃','🎀','🫧','🪣','🏮','🎪','🧩','🪤','🛒']

function IconPicker({ value, onChange }) {
  return (
    <div>
      <div style={{ fontSize: 11, fontWeight: 700, color: '#111317', textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 8, fontFamily: "'JetBrains Mono',monospace" }}>Icon</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(10, 1fr)', gap: 4, background: '#f9f9f7', borderRadius: 8, padding: 8, border: '1px solid #e9e9e4', maxHeight: 140, overflowY: 'auto' }}>
        {SUGGESTED_ICONS.map(icon => (
          <div key={icon} onClick={() => onChange(icon)}
            style={{ width: 28, height: 28, borderRadius: 6, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, cursor: 'pointer', background: value === icon ? '#111317' : 'transparent', border: value === icon ? '2px solid #111317' : '2px solid transparent', transition: 'all 0.1s' }}
            onMouseEnter={e => { if(value !== icon) e.currentTarget.style.background = '#f0f0ed' }}
            onMouseLeave={e => { if(value !== icon) e.currentTarget.style.background = 'transparent' }}>
            {icon}
          </div>
        ))}
      </div>
      {value && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
          <span style={{ fontSize: 20 }}>{value}</span>
          <span style={{ fontSize: 12, color: '#9a9a94' }}>Selected</span>
          <button type="button" onClick={() => onChange('')} style={{ fontSize: 11, color: '#b91c1c', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 700 }}>Clear</button>
        </div>
      )}
    </div>
  )
}

// ── Attribute type options ─────────────────────────────────────────────────────
const ATTR_TYPES = [
  { value: 'select',   label: 'Select (dropdown)',  hint: 'e.g. Red, Blue, Green'     },
  { value: 'text',     label: 'Text (free input)',   hint: 'e.g. custom engraving'     },
  { value: 'number',   label: 'Number',              hint: 'e.g. waist size in cm'     },
  { value: 'boolean',  label: 'Yes / No',            hint: 'e.g. Is Waterproof?'       },
]

// Default attribute presets for quick-add
const ATTR_PRESETS = {
  Fashion:     [{ name:'Size', type:'select', options:['XS','S','M','L','XL','XXL'] }, { name:'Color', type:'select', options:['Red','Blue','Black','White','Green'] }],
  Shoes:       [{ name:'Size', type:'select', options:['36','37','38','39','40','41','42','43','44','45'] }, { name:'Color', type:'select', options:['Black','White','Brown','Beige'] }],
  Electronics: [{ name:'Storage', type:'select', options:['64GB','128GB','256GB','512GB'] }, { name:'Color', type:'select', options:['Black','White','Silver','Gold'] }],
  Generic:     [{ name:'Size', type:'select', options:['Small','Medium','Large'] }, { name:'Color', type:'select', options:['Red','Blue','Black','White'] }],
}

// ── Single attribute definition editor ────────────────────────────────────────
function AttrDefRow({ attr, index, onChange, onDelete }) {
  const [optInput, setOptInput] = useState('')

  const addOption = () => {
    const v = optInput.trim()
    if (!v) return
    const opts = [...(attr.options || [])]
    if (!opts.includes(v)) opts.push(v)
    onChange(index, { ...attr, options: opts })
    setOptInput('')
  }

  const removeOption = (opt) => {
    onChange(index, { ...attr, options: (attr.options || []).filter(o => o !== opt) })
  }

  return (
    <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px 14px 12px' }}>
      {/* Row 1: name + type + delete */}
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 10 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>Attribute Name</div>
          <input
            value={attr.name}
            onChange={e => onChange(index, { ...attr, name: e.target.value })}
            placeholder="e.g. Size, Color"
            style={{ ...input, height: 36, fontSize: 12 }}
          />
        </div>
        <div style={{ width: 140 }}>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 4 }}>Type</div>
          <select
            value={attr.type}
            onChange={e => onChange(index, { ...attr, type: e.target.value, options: e.target.value === 'select' ? (attr.options || []) : [] })}
            style={{ ...input, height: 36, fontSize: 12, cursor: 'pointer' }}
          >
            {ATTR_TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6, paddingTop: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 5 }}>
            <input
              type="checkbox"
              checked={!!attr.required}
              onChange={e => onChange(index, { ...attr, required: e.target.checked })}
              id={`req-${index}`}
              style={{ cursor: 'pointer' }}
            />
            <label htmlFor={`req-${index}`} style={{ fontSize: 11, fontWeight: 600, color: '#374151', cursor: 'pointer', whiteSpace: 'nowrap' }}>Required</label>
          </div>
          <button
            type="button"
            onClick={() => onDelete(index)}
            style={{ padding: '4px 8px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
          >✕</button>
        </div>
      </div>

      {/* Options editor for select type */}
      {attr.type === 'select' && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 6 }}>Options</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 7 }}>
            {(attr.options || []).map(opt => (
              <span key={opt} style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 6, padding: '2px 8px', fontSize: 12, fontWeight: 600 }}>
                {opt}
                <span onClick={() => removeOption(opt)} style={{ cursor: 'pointer', color: '#b91c1c', fontSize: 13, fontWeight: 800, lineHeight: 1 }}>×</span>
              </span>
            ))}
            {(attr.options || []).length === 0 && (
              <span style={{ fontSize: 11, color: '#9a9a94', fontStyle: 'italic' }}>No options yet</span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 6 }}>
            <input
              value={optInput}
              onChange={e => setOptInput(e.target.value)}
              onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addOption() } }}
              placeholder="Type option, press Enter"
              style={{ ...input, height: 32, flex: 1, fontSize: 12 }}
            />
            <button
              type="button"
              onClick={addOption}
              style={{ height: 32, padding: '0 12px', borderRadius: 6, border: 'none', background: '#e8440a', color: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}
            >Add</button>
          </div>
        </div>
      )}
    </div>
  )
}

// ── Category Modal ────────────────────────────────────────────────────────────
function CategoryModal({ cat, onClose, onSaved }) {
  // Parse existing attribute_defs
  const parseAttrDefs = (raw) => {
    if (!raw) return []
    try { return Array.isArray(raw) ? raw : JSON.parse(raw) } catch { return [] }
  }

  const [form, setForm] = useState({
    name:        cat?.name        || '',
    icon:        cat?.icon        || '',
    description: cat?.description || '',
    active:      cat?.active      ?? true,
    stock_mode:  cat?.stock_mode  || 'optional',  // 'optional' | 'required' | 'none'
  })
  const [attrDefs, setAttrDefs] = useState(parseAttrDefs(cat?.attribute_defs))
  const [tab,      setTab]      = useState('basic')   // 'basic' | 'variants'
  const [loading,  setLoading]  = useState(false)
  const [error,    setError]    = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const addAttr = () => {
    setAttrDefs(prev => [...prev, { name: '', type: 'select', options: [], required: false }])
    setTab('variants')
  }

  const updateAttr = (i, updated) => setAttrDefs(prev => prev.map((a, idx) => idx === i ? updated : a))
  const deleteAttr = (i) => setAttrDefs(prev => prev.filter((_, idx) => idx !== i))

  const applyPreset = (key) => {
    const preset = ATTR_PRESETS[key]
    if (preset) setAttrDefs(preset.map(a => ({ ...a, required: false })))
    setTab('variants')
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    // Validate: all attrs must have a name
    const invalid = attrDefs.find(a => !a.name.trim())
    if (invalid) { setError('All attributes must have a name'); setTab('variants'); return }
    // Validate: select attrs must have at least 1 option
    const noOpts = attrDefs.find(a => a.type === 'select' && (!a.options || a.options.length === 0))
    if (noOpts) { setError(`Attribute "${noOpts.name}" needs at least one option`); setTab('variants'); return }

    setLoading(true); setError('')
    try {
      const payload = { ...form, attribute_defs: attrDefs }
      const res = cat?.category_id
        ? await api.put(`/settings/admin/categories/${cat.category_id}`, payload)
        : await api.post('/settings/admin/categories', payload)
      onSaved(res.data.category)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category')
    } finally { setLoading(false) }
  }

  const hasVariants = attrDefs.length > 0

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 560, maxHeight: '90vh', background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease', display: 'flex', flexDirection: 'column' }}>
        <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

        {/* Header */}
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div>
            <div style={{ fontSize: 15, fontWeight: 800 }}>{cat?.category_id ? 'Edit Category' : 'New Category'}</div>
            {hasVariants && <div style={{ fontSize: 11, color: '#e8440a', marginTop: 2 }}>✦ {attrDefs.length} variant attribute{attrDefs.length !== 1 ? 's' : ''} defined</div>}
          </div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', borderBottom: '1px solid #e9e9e4', padding: '0 22px', flexShrink: 0 }}>
          {[['basic','Basic Info'], ['variants', `Variants & Attributes${hasVariants ? ` (${attrDefs.length})` : ''}`]].map(([id, lbl]) => (
            <button key={id} onClick={() => setTab(id)} type="button"
              style={{ padding: '10px 14px', border: 'none', background: 'none', cursor: 'pointer', fontSize: 12, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif", color: tab === id ? '#111317' : '#9a9a94', borderBottom: `2px solid ${tab === id ? '#e8440a' : 'transparent'}`, marginBottom: -1 }}>
              {lbl}
            </button>
          ))}
        </div>

        <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
          <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>

            {/* ── Basic Info Tab ── */}
            {tab === 'basic' && (
              <>
                <div>
                  <label style={label}>Category Name *</label>
                  <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Fashion" style={input} />
                </div>
                <IconPicker value={form.icon} onChange={v => set('icon', v)} />
                <div>
                  <label style={label}>Description</label>
                  <input value={form.description} onChange={e => set('description', e.target.value)} placeholder="Brief description..." style={input} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Active</div>
                    <div style={hint}>Visible to sellers and buyers</div>
                  </div>
                  <Toggle checked={form.active} onChange={v => set('active', v)} />
                </div>

                {/* Stock mode */}
                <div>
                  <label style={label}>Stock Tracking</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[
                      { v: 'optional', l: 'Optional', h: 'Seller can choose' },
                      { v: 'required', l: 'Required', h: 'Must track stock'  },
                      { v: 'none',     l: 'Off',      h: 'No stock tracking' },
                    ].map(({ v, l, h }) => (
                      <div key={v} onClick={() => set('stock_mode', v)}
                        style={{ flex: 1, padding: '8px 10px', borderRadius: 8, border: `1.5px solid ${form.stock_mode === v ? '#e8440a' : '#e9e9e4'}`, background: form.stock_mode === v ? '#fff3f0' : '#fff', cursor: 'pointer', textAlign: 'center' }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: form.stock_mode === v ? '#e8440a' : '#374151' }}>{l}</div>
                        <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 2 }}>{h}</div>
                      </div>
                    ))}
                  </div>
                </div>

                {/* Quick link to variants */}
                <div style={{ background: '#f9f9f7', borderRadius: 8, padding: '10px 14px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                  <div style={{ fontSize: 12, color: '#374151' }}>
                    {hasVariants
                      ? <><b style={{ color: '#111317' }}>{attrDefs.length} variant attribute{attrDefs.length !== 1 ? 's' : ''}</b> defined (e.g. {attrDefs.slice(0,2).map(a => a.name).join(', ')})</>
                      : <span style={{ color: '#9a9a94' }}>No variant attributes yet — products will use a single price & quantity</span>}
                  </div>
                  <button type="button" onClick={() => setTab('variants')}
                    style={{ fontSize: 11, fontWeight: 700, color: '#e8440a', background: 'none', border: '1px solid #e8440a', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', whiteSpace: 'nowrap' }}>
                    {hasVariants ? 'Edit Variants' : '+ Add Variants'}
                  </button>
                </div>
              </>
            )}

            {/* ── Variants Tab ── */}
            {tab === 'variants' && (
              <>
                {/* Explainer */}
                <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 14px', fontSize: 12, color: '#0369a1', lineHeight: 1.6 }}>
                  <b>Variant attributes</b> define what options a product in this category can have.
                  Sellers will combine these to create variants like <b>Size M + Color Red</b>.
                  Each variant gets its own price and quantity.
                </div>

                {/* Preset buttons */}
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 8 }}>Quick Presets</div>
                  <div style={{ display: 'flex', gap: 7, flexWrap: 'wrap' }}>
                    {Object.keys(ATTR_PRESETS).map(key => (
                      <button key={key} type="button" onClick={() => applyPreset(key)}
                        style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#374151', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                        {key}
                      </button>
                    ))}
                    <button type="button" onClick={() => setAttrDefs([])}
                      style={{ padding: '5px 12px', borderRadius: 6, border: '1px solid #fecaca', background: '#fef2f2', fontSize: 12, fontWeight: 600, cursor: 'pointer', color: '#b91c1c', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      Clear All
                    </button>
                  </div>
                </div>

                {/* Attribute list */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {attrDefs.length === 0 && (
                    <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '20px 0', border: '1px dashed #e9e9e4', borderRadius: 8 }}>
                      No attributes yet. Use a preset or add one manually.
                    </div>
                  )}
                  {attrDefs.map((attr, i) => (
                    <AttrDefRow key={i} attr={attr} index={i} onChange={updateAttr} onDelete={deleteAttr} />
                  ))}
                </div>

                <button type="button" onClick={addAttr}
                  style={{ height: 36, borderRadius: 8, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  + Add Attribute
                </button>

                {/* Preview */}
                {attrDefs.length > 0 && attrDefs.every(a => a.name && (a.type !== 'select' || a.options?.length > 0)) && (
                  <div style={{ background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 10, padding: '14px' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.07em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 10 }}>Product Variant Preview</div>
                    <div style={{ fontFamily: "'JetBrains Mono',monospace", fontSize: 11, color: '#374151', background: '#fff', border: '1px solid #e9e9e4', borderRadius: 6, padding: '10px 12px', whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
{`const product = {
  name: "${form.name || 'Product'} example",
  hasVariants: true,
  variants: [
    {
      attributes: { ${attrDefs.map(a =>
        `${a.name}: '${a.type === 'select' ? (a.options[0] || 'Option1') : a.type === 'number' ? '42' : a.type === 'boolean' ? 'true' : 'value'}'`
      ).join(', ')} },
      price: 5000,
      quantity: 10,
    },
    {
      attributes: { ${attrDefs.map(a =>
        `${a.name}: '${a.type === 'select' ? (a.options[1] || a.options[0] || 'Option2') : a.type === 'number' ? '44' : a.type === 'boolean' ? 'false' : 'other'}'`
      ).join(', ')} },
      price: 5500,
      quantity: 5,
    },
  ],
}`}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>

          {error && (
            <div style={{ margin: '0 22px', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>
          )}

          <div style={{ padding: '12px 22px 20px', display: 'flex', gap: 10, flexShrink: 0, borderTop: '1px solid #e9e9e4', marginTop: 8 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : cat?.category_id ? 'Save Changes' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Creator Category Item — lighter than CategoryItem, no variant attrs ──────
function CreatorCategoryItem({ cat, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
      {cat.icon && <span style={{ fontSize: 20 }}>{cat.icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{cat.name}</div>
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 3, background: cat.active ? '#f0fdf4' : '#fef2f2', color: cat.active ? '#15803d' : '#b91c1c' }}>
        {cat.active ? 'Active' : 'Hidden'}
      </div>
      <button onClick={() => onEdit(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Edit</button>
      <button onClick={() => onDelete(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
    </div>
  )
}

// ── Creator Category Modal — name/icon/active/sort_order only ────────────────
function CreatorCategoryModal({ cat, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:       cat?.name       || '',
    icon:       cat?.icon       || '',
    active:     cat?.active     ?? true,
    sort_order: cat?.sort_order ?? 0,
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    setLoading(true); setError('')
    try {
      const payload = { name: form.name.trim(), icon: form.icon || null, active: form.active, sort_order: Number(form.sort_order) || 0 }
      const res = cat?.category_id
        ? await api.put(`/creators/admin/categories/${cat.category_id}`, payload)
        : await api.post('/creators/admin/categories', payload)
      onSaved(res.data.category)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category')
    } finally { setLoading(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{cat?.category_id ? 'Edit Creator Category' : 'New Creator Category'}</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>
        <form onSubmit={handleSave}>
          <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <IconPicker value={form.icon} onChange={v => set('icon', v)} />
            <div>
              <label style={label}>Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Fashion, Comedy, Music" style={input} />
            </div>
            <div>
              <label style={label}>Sort Order</label>
              <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} style={{ ...input, maxWidth: 140 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Active</div>
              <Toggle checked={!!form.active} onChange={v => set('active', v)} />
            </div>
            {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>}
          </div>
          <div style={{ padding: '12px 22px 20px', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : cat?.category_id ? 'Save Changes' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Video Category Item — same shape as CreatorCategoryItem ──────────────────
function VideoCategoryItem({ cat, onEdit, onDelete }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
      {cat.icon && <span style={{ fontSize: 20 }}>{cat.icon}</span>}
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, fontWeight: 700 }}>{cat.name}</div>
      </div>
      <div style={{ fontSize: 10, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", padding: '2px 7px', borderRadius: 3, background: cat.active ? '#f0fdf4' : '#fef2f2', color: cat.active ? '#15803d' : '#b91c1c' }}>
        {cat.active ? 'Active' : 'Hidden'}
      </div>
      <button onClick={() => onEdit(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Edit</button>
      <button onClick={() => onDelete(cat)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
    </div>
  )
}

// ── Video Category Modal — name/icon/active/sort_order only ──────────────────
function VideoCategoryModal({ cat, onClose, onSaved }) {
  const [form, setForm] = useState({
    name:       cat?.name       || '',
    icon:       cat?.icon       || '',
    active:     cat?.active     ?? true,
    sort_order: cat?.sort_order ?? 0,
  })
  const [loading, setLoading] = useState(false)
  const [error,   setError]   = useState('')
  const set = (k, v) => setForm(f => ({ ...f, [k]: v }))

  const handleSave = async (e) => {
    e.preventDefault()
    if (!form.name.trim()) { setError('Name is required'); return }
    setLoading(true); setError('')
    try {
      const payload = { name: form.name.trim(), icon: form.icon || null, active: form.active, sort_order: Number(form.sort_order) || 0 }
      const res = cat?.category_id
        ? await api.put(`/videos/admin/categories/${cat.category_id}`, payload)
        : await api.post('/videos/admin/categories', payload)
      onSaved(res.data.category)
      onClose()
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save category')
    } finally { setLoading(false) }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 500 }} />
      <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 501, boxShadow: '0 24px 80px rgba(0,0,0,0.2)', overflow: 'hidden' }}>
        <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ fontSize: 15, fontWeight: 800 }}>{cat?.category_id ? 'Edit Video Category' : 'New Video Category'}</div>
          <div onClick={onClose} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
        </div>
        <form onSubmit={handleSave}>
          <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
            <IconPicker value={form.icon} onChange={v => set('icon', v)} />
            <div>
              <label style={label}>Name *</label>
              <input required value={form.name} onChange={e => set('name', e.target.value)} placeholder="e.g. Movie, Documentary, Music Video" style={input} />
            </div>
            <div>
              <label style={label}>Sort Order</label>
              <input type="number" value={form.sort_order} onChange={e => set('sort_order', e.target.value)} style={{ ...input, maxWidth: 140 }} />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Active</div>
              <Toggle checked={!!form.active} onChange={v => set('active', v)} />
            </div>
            {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 7, padding: '9px 13px', fontSize: 12, color: '#b91c1c', fontWeight: 600 }}>⚠ {error}</div>}
          </div>
          <div style={{ padding: '12px 22px 20px', display: 'flex', gap: 10 }}>
            <button type="button" onClick={onClose} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
            <button type="submit" disabled={loading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: loading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
              {loading ? 'Saving…' : cat?.category_id ? 'Save Changes' : 'Create Category'}
            </button>
          </div>
        </form>
      </div>
    </>
  )
}

// ── Delivery Zone Item ────────────────────────────────────────────────────────
const DELIVERY_TIME_LABELS = {
  same_day : 'Same Day',
  next_day : 'Next Day',
  '2-3_days': '2–3 Days',
  '3-5_days': '3–5 Days',
  '1_week'  : '1 Week',
}

function ZoneItem({ zone, onEdit, onDelete }) {
  const available = zone.active !== false
  const timeLabel = DELIVERY_TIME_LABELS[zone.delivery_time] || zone.delivery_time || '—'
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 14px', background: available ? '#fafaf8' : '#fef9f9', borderRadius: 8, border: `1px solid ${available ? '#e9e9e4' : '#fde8e8'}` }}>
      {/* Available dot */}
      <div style={{ width: 8, height: 8, borderRadius: '50%', background: available ? '#16a34a' : '#dc2626', flexShrink: 0, boxShadow: available ? '0 0 0 2px rgba(22,163,74,0.2)' : '0 0 0 2px rgba(220,38,38,0.2)' }} />

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 700 }}>{zone.zone_name}</div>
          <span style={{ fontSize: 10, fontWeight: 700, color: available ? '#16a34a' : '#dc2626', background: available ? '#f0fdf4' : '#fef2f2', border: `1px solid ${available ? '#bbf7d0' : '#fecaca'}`, borderRadius: 4, padding: '1px 6px', fontFamily: "'JetBrains Mono',monospace" }}>
            {available ? 'ACTIVE' : 'INACTIVE'}
          </span>
        </div>
        {(zone.city || zone.district || zone.area) && (
          <div style={{ fontSize: 11, color: '#6b7280', marginTop: 3, fontFamily: "'JetBrains Mono',monospace", display: 'flex', gap: 6, flexWrap: 'wrap' }}>
            {zone.city     && <span>{zone.city}</span>}
            {zone.district && <><span style={{ color: '#d1d5db' }}>·</span><span>{zone.district}</span></>}
            {zone.area     && <><span style={{ color: '#d1d5db' }}>·</span><span>{zone.area}</span></>}
          </div>
        )}
        {!zone.city && <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 2 }}>{zone.description || '—'}</div>}
      </div>

      {/* Delivery time */}
      <div style={{ textAlign: 'center', minWidth: 64 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{timeLabel}</div>
        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>delivery</div>
      </div>

      {/* Fee */}
      <div style={{ textAlign: 'right' }}>
        <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace", color: '#111317' }}>{Number(zone.fee).toLocaleString()} RWF</div>
        <div style={{ fontSize: 10, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace" }}>base fee</div>
      </div>
      {zone.per_km_fee > 0 && (
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 13, fontWeight: 700, fontFamily: "'JetBrains Mono',monospace", color: '#374151' }}>+{Number(zone.per_km_fee).toLocaleString()}/km</div>
        </div>
      )}
      <button onClick={() => onEdit(zone)} style={{ padding: '4px 10px', borderRadius: 6, border: '1px solid #e9e9e4', background: '#fff', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Edit</button>
      <button onClick={() => onDelete(zone)} style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Del</button>
    </div>
  )
}


// ── Tip Adder — emoji picker + amount input in one row ────────────────────────
const TIP_EMOJI_LIST = ['❤️','🔥','👏','💎','🎁','🌟','💰','🎉','🦋','⚡','🍀','🎵']

function TipAdder({ onAdd }) {
  const [selectedEmoji, setSelectedEmoji] = useState(TIP_EMOJI_LIST[0])
  const [amount,        setAmount]        = useState('')
  const [showPicker,    setShowPicker]    = useState(false)

  const handleAdd = () => {
    const v = Number(amount)
    if (!v || v <= 0) return
    onAdd(selectedEmoji, v)
    setAmount('')
  }

  return (
    <div>
      <label style={label}>Add New Tip Button</label>
      <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>

        {/* Emoji picker trigger */}
        <div style={{ position: 'relative' }}>
          <div onClick={() => setShowPicker(p => !p)}
            style={{ width: 48, height: 40, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 22, cursor: 'pointer', userSelect: 'none' }}>
            {selectedEmoji}
          </div>
          {showPicker && (
            <>
              <div onClick={() => setShowPicker(false)} style={{ position: 'fixed', inset: 0, zIndex: 10 }} />
              <div style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, background: '#fff', border: '1px solid #e9e9e4', borderRadius: 10, boxShadow: '0 10px 30px rgba(0,0,0,0.12)', zIndex: 11, padding: 10 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#9a9a94', fontFamily: "'JetBrains Mono',monospace", textTransform: 'uppercase', marginBottom: 8 }}>Pick Emoji</div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(6, 1fr)', gap: 4 }}>
                  {TIP_EMOJI_LIST.map(emoji => (
                    <div key={emoji}
                      onClick={() => { setSelectedEmoji(emoji); setShowPicker(false) }}
                      style={{ width: 36, height: 36, borderRadius: 8, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, cursor: 'pointer', background: selectedEmoji === emoji ? '#fff3f0' : 'transparent', border: selectedEmoji === emoji ? '1.5px solid #e8440a' : '1.5px solid transparent' }}
                      onMouseEnter={e => { if (selectedEmoji !== emoji) e.currentTarget.style.background = '#f9f9f7' }}
                      onMouseLeave={e => { if (selectedEmoji !== emoji) e.currentTarget.style.background = 'transparent' }}>
                      {emoji}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

        {/* Amount input */}
        <input
          type="number"
          min="1"
          value={amount}
          onChange={e => setAmount(e.target.value)}
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAdd() } }}
          placeholder="Amount in RWF (e.g. 500)"
          style={{ ...input, flex: 1 }}
        />

        {/* Add button */}
        <button type="button" onClick={handleAdd}
          disabled={!amount || Number(amount) <= 0}
          style={{ height: 40, padding: '0 18px', borderRadius: 8, border: 'none', background: (!amount || Number(amount) <= 0) ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 12, fontWeight: 700, cursor: (!amount || Number(amount) <= 0) ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          + Add
        </button>
      </div>
      <p style={hint}>Click the emoji to change it, then type the amount and press Enter or click Add.</p>
    </div>
  )
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function Settings() {
  // ── Config state ──
  const [config, setConfig] = useState({
    // Fees
    platform_commission_pct:  0.01,
    ipay_fee_pct:             0.04,
    driver_commission_pct:    0,
    vat_pct:                  0,
    vat_enabled:              false,
    min_order_amount:         1000,
    free_delivery_threshold:  0,
    // Limits
    max_products_per_shop:    100,
    max_reels_per_day:        10,
    max_images_per_product:   8,
    max_cart_items:           20,
    order_expiry_minutes:     30,
    refund_window_hours:      48,
    invoice_expiry_minutes:   10,
    // Reel/Live
    reel_max_duration_secs:   60,
    live_max_duration_mins:   180,
    quick_live_confirm_secs:  60,
    // Platform
    maintenance_mode:         false,
    maintenance_message:      'We are currently under maintenance. Be right back!',
    allow_seller_registration: true,
    allow_driver_registration: true,
    // Payment methods
    payment_mtn_enabled:      true,
    payment_airtel_enabled:   true,
    payment_ipay_enabled:     true,
    // Notifications
    notify_new_order:         true,
    notify_low_stock:         true,
    low_stock_threshold:      5,
    // Live tips
    tips_enabled:             true,
    tip_amounts:              [100, 200, 500, 1000, 2000, 5000],
    tip_emojis:               ['❤️', '🔥', '👏', '💎', '🎁', '🌟'],
    // App info
    app_name:                 'Kudos Live Market',
    support_phone:            '',
    support_email:            '',
    app_version:              '1.0.0',
    // Auto-cancel failed orders
    auto_cancel_failed_enabled:       true,
    auto_cancel_failed_hours:         48,
    auto_cancel_check_interval_hours: 1,
    // Seller tiers — qualification thresholds (either revenue OR orders qualifies)
    tier_silver_min_revenue:          500000,
    tier_silver_min_orders:           50,
    tier_silver_commission_discount:  0.01,
    tier_gold_min_revenue:            2000000,
    tier_gold_min_orders:             200,
    tier_gold_commission_discount:    0.02,
    tier_platinum_min_revenue:        5000000,
    tier_platinum_min_orders:         500,
    tier_platinum_commission_discount: 0.03,
    // Creator monetization — tip commission + view-based tiers
    tip_commission_pct:                       0.10,
    tip_payout_min_amount:                     5000,
    unlock_commission_pct:                     0.20,
    creator_tier_silver_min_views:             5000,
    creator_tier_silver_commission_discount:   0.01,
    creator_tier_gold_min_views:                25000,
    creator_tier_gold_commission_discount:      0.02,
    creator_tier_platinum_min_views:            100000,
    creator_tier_platinum_commission_discount:  0.03,
    // Video Pass — time-boxed all-access, one-time purchase
    video_pass_weekly_price:   2000,
    video_pass_weekly_days:    7,
    video_pass_monthly_price:  6000,
    video_pass_monthly_days:   30,
    video_pass_creator_share_pct: 0.75,
  })

  const [categories,        setCategories]        = useState([])
  const [creatorCategories, setCreatorCategories] = useState([])
  const [videoCategories,   setVideoCategories]   = useState([])
  const [zones,        setZones]        = useState([])
  const [loading,      setLoading]      = useState(true)
  const [saving,       setSaving]       = useState(false)
  const [saved,        setSaved]        = useState(false)
  const [error,        setError]        = useState('')
  const [catModal,     setCatModal]     = useState(null)   // null | {} | category
  const [creatorCatModal, setCreatorCatModal] = useState(null)
  const [videoCatModal,   setVideoCatModal]   = useState(null)
  const [zoneModal,    setZoneModal]    = useState(null)
  const [zoneForm,     setZoneForm]     = useState({ zone_name:'', description:'', fee:'', per_km_fee:'', available: true, delivery_time: 'same_day', city:'', district:'', area:'', lat:null, lng:null, formatted_address:'' })
  const [zoneLoading,  setZoneLoading]  = useState(false)
  const [lastUpdated,  setLastUpdated]  = useState(null)

  // ── Fetch all settings ──
  const fetchSettings = useCallback(async () => {
    setLoading(true)
    try {
      const [cfgRes, catRes, creatorCatRes, videoCatRes, zoneRes] = await Promise.all([
        api.get('/settings/admin/config'),
        api.get('/settings/admin/categories'),
        api.get('/creators/admin/categories'),
        api.get('/videos/admin/categories'),
        api.get('/settings/admin/delivery-zones'),
      ])
      if (cfgRes.data.config) setConfig(prev => ({ ...prev, ...cfgRes.data.config }))
      setCategories(catRes.data.categories || [])
      setCreatorCategories(creatorCatRes.data.categories || [])
      setVideoCategories(videoCatRes.data.categories || [])
      setZones(zoneRes.data.zones || [])
      setLastUpdated(cfgRes.data.updated_at || null)
    } catch {
      // Use defaults — mock some categories and zones
      setCategories([
        { category_id: 1, name: 'Fashion',     icon: '👗', description: 'Clothing & accessories', active: true },
        { category_id: 2, name: 'Electronics', icon: '📱', description: 'Phones, gadgets & more',  active: true },
        { category_id: 3, name: 'Beauty',      icon: '💄', description: 'Cosmetics & skincare',    active: true },
        { category_id: 4, name: 'Food',        icon: '🍕', description: 'Local food & beverages',  active: true },
        { category_id: 5, name: 'Sports',      icon: '⚽', description: 'Sports & fitness gear',   active: false },
      ])
      setZones([
        { zone_id: 1, zone_name: 'Kigali Central',  description: 'CBD & Nyarugenge',  fee: 1000, per_km_fee: 0   },
        { zone_id: 2, zone_name: 'Kigali Suburbs',  description: 'Gasabo & Kicukiro', fee: 1500, per_km_fee: 200 },
        { zone_id: 3, zone_name: 'Outer Kigali',    description: 'Beyond 15km',       fee: 3000, per_km_fee: 300 },
      ])
    } finally { setLoading(false) }
  }, [])

  useEffect(() => { fetchSettings() }, [fetchSettings])

  // ── Tier discount validation (derived — no extra state needed) ──
  const basePct  = Number(config.platform_commission_pct || 0)
  const discS    = Number(config.tier_silver_commission_discount   || 0)
  const discG    = Number(config.tier_gold_commission_discount     || 0)
  const discP    = Number(config.tier_platinum_commission_discount || 0)
  const tierDiscErrors = {
    silver:   discS >= basePct
      ? `Must be less than platform commission (${(basePct*100).toFixed(1)}%)`
      : '',
    gold:     discG >= basePct
      ? `Must be less than platform commission (${(basePct*100).toFixed(1)}%)`
      : discG < discS ? 'Gold discount must be ≥ Silver discount' : '',
    platinum: discP >= basePct
      ? `Must be less than platform commission (${(basePct*100).toFixed(1)}%)`
      : discP < discG ? 'Platinum discount must be ≥ Gold discount' : '',
  }
  const hasTierErrors = Object.values(tierDiscErrors).some(Boolean)

  // ── Creator tier discount validation — same shape, vs tip_commission_pct ──
  const tipBasePct = Number(config.tip_commission_pct || 0)
  const cDiscS = Number(config.creator_tier_silver_commission_discount   || 0)
  const cDiscG = Number(config.creator_tier_gold_commission_discount     || 0)
  const cDiscP = Number(config.creator_tier_platinum_commission_discount || 0)
  const creatorTierDiscErrors = {
    silver:   cDiscS >= tipBasePct
      ? `Must be less than tip commission (${(tipBasePct*100).toFixed(1)}%)`
      : '',
    gold:     cDiscG >= tipBasePct
      ? `Must be less than tip commission (${(tipBasePct*100).toFixed(1)}%)`
      : cDiscG < cDiscS ? 'Gold discount must be ≥ Silver discount' : '',
    platinum: cDiscP >= tipBasePct
      ? `Must be less than tip commission (${(tipBasePct*100).toFixed(1)}%)`
      : cDiscP < cDiscG ? 'Platinum discount must be ≥ Gold discount' : '',
  }
  const hasCreatorTierErrors = Object.values(creatorTierDiscErrors).some(Boolean)

  // ── Save config ──
  const handleSaveConfig = async () => {
    if (hasTierErrors || hasCreatorTierErrors) return
    setSaving(true); setError(''); setSaved(false)
    try {
      await api.put('/settings/admin/config', config)
      setSaved(true)
      setLastUpdated(new Date().toISOString())
      setTimeout(() => setSaved(false), 3000)
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save settings')
    } finally { setSaving(false) }
  }

  const set = (k, v) => setConfig(prev => ({ ...prev, [k]: v }))

  // ── Category handlers ──
  const handleCatSaved = (cat) => {
    setCategories(prev => {
      const exists = prev.find(c => c.category_id === cat.category_id)
      return exists ? prev.map(c => c.category_id === cat.category_id ? cat : c) : [...prev, cat]
    })
  }
  const handleCatDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Shops using it will become uncategorized.`)) return
    try {
      await api.delete(`/settings/admin/categories/${cat.category_id}`)
      setCategories(prev => prev.filter(c => c.category_id !== cat.category_id))
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete') }
  }

  // ── Creator category handlers ──
  const handleCreatorCatSaved = (cat) => {
    setCreatorCategories(prev => {
      const exists = prev.find(c => c.category_id === cat.category_id)
      return exists ? prev.map(c => c.category_id === cat.category_id ? cat : c) : [...prev, cat]
    })
  }
  const handleCreatorCatDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Creators using it will become uncategorized.`)) return
    try {
      await api.delete(`/creators/admin/categories/${cat.category_id}`)
      setCreatorCategories(prev => prev.filter(c => c.category_id !== cat.category_id))
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete') }
  }

  // ── Video category handlers ──
  const handleVideoCatSaved = (cat) => {
    setVideoCategories(prev => {
      const exists = prev.find(c => c.category_id === cat.category_id)
      return exists ? prev.map(c => c.category_id === cat.category_id ? cat : c) : [...prev, cat]
    })
  }
  const handleVideoCatDelete = async (cat) => {
    if (!window.confirm(`Delete category "${cat.name}"? Videos using it will become uncategorized.`)) return
    try {
      await api.delete(`/videos/admin/categories/${cat.category_id}`)
      setVideoCategories(prev => prev.filter(c => c.category_id !== cat.category_id))
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete') }
  }

  // ── Zone handlers ──
  const handleZoneSave = async (e) => {
    e.preventDefault(); setZoneLoading(true)
    try {
      const payload = { ...zoneForm, fee: Number(zoneForm.fee), per_km_fee: Number(zoneForm.per_km_fee || 0), available: zoneForm.available, delivery_time: zoneForm.delivery_time }
      const res = zoneModal?.zone_id
        ? await api.put(`/settings/admin/delivery-zones/${zoneModal.zone_id}`, payload)
        : await api.post('/settings/admin/delivery-zones', payload)
      const saved = res.data.zone
      setZones(prev => {
        const exists = prev.find(z => z.zone_id === saved.zone_id)
        return exists ? prev.map(z => z.zone_id === saved.zone_id ? saved : z) : [...prev, saved]
      })
      setZoneModal(null)
    } catch (err) { alert(err.response?.data?.error || 'Failed to save zone') }
    finally { setZoneLoading(false) }
  }
  const handleZoneDelete = async (zone) => {
    if (!window.confirm(`Delete zone "${zone.zone_name}"?`)) return
    try {
      await api.delete(`/settings/admin/delivery-zones/${zone.zone_id}`)
      setZones(prev => prev.filter(z => z.zone_id !== zone.zone_id))
    } catch (err) { alert(err.response?.data?.error || 'Failed to delete zone') }
  }
  const openZoneEdit = (zone) => {
    setZoneForm({ zone_name: zone.zone_name, description: zone.description||'', fee: zone.fee, per_km_fee: zone.per_km_fee||'', available: zone.active !== false, delivery_time: zone.delivery_time || 'same_day', city: zone.city||'', district: zone.district||'', area: zone.area||'', lat: zone.lat||null, lng: zone.lng||null, formatted_address: zone.formatted_address||'' })
    setZoneModal(zone)
  }

  if (loading) {
    return <div style={{ padding: '60px 32px', textAlign: 'center', color: '#9a9a94', fontSize: 14 }}>Loading settings…</div>
  }

  return (
    <div style={{ padding: '28px 32px 80px', fontFamily: "'Plus Jakarta Sans', sans-serif", maxWidth: 860 }}>
      <style>{`@keyframes popIn{from{opacity:0;transform:translate(-50%,-48%) scale(0.97)}to{opacity:1;transform:translate(-50%,-50%) scale(1)}}`}</style>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 28 }}>
        <div>
          <div style={{ fontSize: 22, fontWeight: 800, letterSpacing: '-0.4px' }}>App Configuration</div>
          <div style={{ fontSize: 13, color: '#9a9a94', marginTop: 4 }}>
            Control all platform settings without touching code.
            {lastUpdated && <span> · Last saved: <b>{fmtTime(lastUpdated)}</b></span>}
          </div>
        </div>
        <button onClick={handleSaveConfig} disabled={saving}
          style={{ height: 40, padding: '0 22px', borderRadius: 9, border: 'none', background: saved ? '#15803d' : saving ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: saving ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif", transition: 'background 0.2s', boxShadow: '0 2px 8px rgba(232,68,10,0.2)' }}>
          {saved ? '✓ Saved!' : saving ? 'Saving…' : 'Save All Settings'}
        </button>
      </div>

      {error && <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', marginBottom: 16, fontSize: 13, color: '#b91c1c' }}>⚠ {error}</div>}

      {/* ── 1. FEES ── */}
      <Section icon="💰" title="Fees & Commission" description="Platform and payment processing fees applied to all orders">
        <Grid cols={3}>
          <div>
            <label style={label}>Platform Commission (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.platform_commission_pct}
              onChange={e => set('platform_commission_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>Platform cut from product sales, e.g. 0.01 = 1% (currently {(Number(config.platform_commission_pct || 0) * 100).toFixed(1)}%)</p>
          </div>
          <div>
            <label style={label}>Driver Fee Commission (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.driver_commission_pct}
              onChange={e => set('driver_commission_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>Platform cut from delivery fees, e.g. 0.01 = 1% (currently {(Number(config.driver_commission_pct || 0) * 100).toFixed(1)}%). Set 0 if drivers keep 100%</p>
          </div>
          <div>
            <label style={label}>iPay / Irembo Fee (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.ipay_fee_pct}
              onChange={e => set('ipay_fee_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>iPay's processing fee, e.g. 0.04 = 4% (currently {(Number(config.ipay_fee_pct || 0) * 100).toFixed(1)}%)</p>
          </div>
          <div>
            <label style={label}>VAT (decimal)</label>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
              <input type="number" min="0" max="1" step="0.01" value={config.vat_pct}
                onChange={e => set('vat_pct', parseFloat(e.target.value))}
                style={{ ...input, flex: 1, opacity: config.vat_enabled ? 1 : 0.5 }}
                disabled={!config.vat_enabled} />
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
                <Toggle checked={!!config.vat_enabled} onChange={v => set('vat_enabled', v)} />
                <span style={{ fontSize: 11, fontWeight: 700, color: config.vat_enabled ? '#15803d' : '#9a9a94', minWidth: 46 }}>
                  {config.vat_enabled ? 'Active' : 'Inactive'}
                </span>
              </div>
            </div>
            <p style={hint}>e.g. 0.18 = 18%{config.vat_enabled ? ` (currently ${(Number(config.vat_pct || 0) * 100).toFixed(1)}% applied on top of order total)` : ' — VAT is disabled, toggle to enable'}</p>
          </div>
          <div>
            <label style={label}>Min Order Amount (RWF)</label>
            <input type="number" min="0" value={config.min_order_amount}
              onChange={e => set('min_order_amount', parseInt(e.target.value))} style={input} />
            <p style={hint}>Buyers cannot checkout below this amount</p>
          </div>
          <div>
            <label style={label}>Free Delivery Threshold (RWF)</label>
            <input type="number" min="0" value={config.free_delivery_threshold}
              onChange={e => set('free_delivery_threshold', parseInt(e.target.value))} style={input} />
            <p style={hint}>Orders above this get free delivery. Set 0 to disable</p>
          </div>
        </Grid>
      </Section>

      {/* ── 1b. CREATOR MONETIZATION ── */}
      <Section icon="🎥" title="Creator Monetization" description="Platform commission deducted from tips and video unlocks sent to creators (buyers who go live without a shop), and the minimum balance before a payout can be requested.">
        <Grid cols={3}>
          <div>
            <label style={label}>Tip Commission (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.tip_commission_pct}
              onChange={e => set('tip_commission_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>Platform cut from tips, e.g. 0.10 = 10% (currently {(Number(config.tip_commission_pct || 0) * 100).toFixed(0)}%). Discounted per creator tier below.</p>
          </div>
          <div>
            <label style={label}>Video Unlock Commission (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.unlock_commission_pct}
              onChange={e => set('unlock_commission_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>Platform cut from video unlock purchases, e.g. 0.20 = 20% (currently {(Number(config.unlock_commission_pct || 0) * 100).toFixed(0)}%). Same creator-tier discounts below also apply here.</p>
          </div>
          <div>
            <label style={label}>Minimum Payout (RWF)</label>
            <input type="number" min="0" value={config.tip_payout_min_amount}
              onChange={e => set('tip_payout_min_amount', parseInt(e.target.value))} style={input} />
            <p style={hint}>A creator's payable balance (tips + video unlocks) must reach this before they can request a payout</p>
          </div>
        </Grid>
      </Section>

      {/* ── 1b. KUDOS PREMIUM ── */}
      <Section icon="🎟" title="Kudos Premium" description="One-time purchase that grants a viewer unlimited access to every video for a fixed number of days — no per-video purchase needed while it's active. Kudos Premium revenue is pooled monthly and paid out to creators by their share of pass-holder watch-time.">
        <Grid cols={2}>
          <div>
            <label style={label}>Weekly Kudos Premium Price (RWF)</label>
            <input type="number" min="0" value={config.video_pass_weekly_price}
              onChange={e => set('video_pass_weekly_price', parseInt(e.target.value))} style={input} />
            <p style={hint}>Currently RWF {Number(config.video_pass_weekly_price || 0).toLocaleString()}</p>
          </div>
          <div>
            <label style={label}>Weekly Kudos Premium Duration (days)</label>
            <input type="number" min="1" value={config.video_pass_weekly_days}
              onChange={e => set('video_pass_weekly_days', parseInt(e.target.value))} style={input} />
          </div>
          <div>
            <label style={label}>Monthly Kudos Premium Price (RWF)</label>
            <input type="number" min="0" value={config.video_pass_monthly_price}
              onChange={e => set('video_pass_monthly_price', parseInt(e.target.value))} style={input} />
            <p style={hint}>Currently RWF {Number(config.video_pass_monthly_price || 0).toLocaleString()}</p>
          </div>
          <div>
            <label style={label}>Monthly Kudos Premium Duration (days)</label>
            <input type="number" min="1" value={config.video_pass_monthly_days}
              onChange={e => set('video_pass_monthly_days', parseInt(e.target.value))} style={input} />
          </div>
          <div>
            <label style={label}>Creator Revenue Share (decimal)</label>
            <input type="number" min="0" max="1" step="0.01" value={config.video_pass_creator_share_pct}
              onChange={e => set('video_pass_creator_share_pct', parseFloat(e.target.value))} style={input} />
            <p style={hint}>Share of pooled Kudos Premium revenue paid out to creators each month, split by watch-time, e.g. 0.75 = 75% (currently {(Number(config.video_pass_creator_share_pct || 0) * 100).toFixed(0)}%). The rest stays as platform income.</p>
          </div>
        </Grid>
      </Section>

      {/* ── 2. PAYMENT METHODS ── */}
      <Section icon="🏦" title="Payment Gateway" description="iPay / Irembo processes all payments — accepts MTN MoMo and Airtel Money automatically">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 8, padding: '12px 16px', fontSize: 12, color: '#0369a1' }}>
            ℹ iPay / Irembo is your payment processor. It automatically accepts MTN MoMo and Airtel Money — no separate toggles needed.
          </div>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>iPay / Irembo Gateway</div>
              <div style={hint}>Accepts MTN MoMo · Airtel Money · Card — all in one</div>
            </div>
            <Toggle checked={!!config['payment_ipay_enabled']} onChange={v => set('payment_ipay_enabled', v)} />
          </div>
          <div>
            <label style={label}>MoMo Invoice Expiry (minutes)</label>
            <input type="number" min="1" max="60" value={config.invoice_expiry_minutes}
              onChange={e => set('invoice_expiry_minutes', parseInt(e.target.value))} style={{ ...input, maxWidth: 200 }} />
            <p style={hint}>Minutes before a MoMo payment invoice expires (currently {config.invoice_expiry_minutes} min)</p>
          </div>
        </div>
      </Section>

      {/* ── 3. DELIVERY ZONES ── */}
      <Section icon="🚚" title="Delivery Zones & Fees" description="Set base delivery fees per zone. Drivers see these as starting prices.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {zones.length === 0
            ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0' }}>No delivery zones yet.</div>
            : zones.map(z => <ZoneItem key={z.zone_id} zone={z} onEdit={openZoneEdit} onDelete={handleZoneDelete} />)}
        </div>
        <button onClick={() => { setZoneForm({ zone_name:'', description:'', fee:'', per_km_fee:'', available: true, delivery_time: 'same_day', city:'', district:'', area:'', lat:null, lng:null, formatted_address:'' }); setZoneModal({}) }}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          + Add Zone
        </button>
      </Section>

      {/* ── 4. CATEGORIES ── */}
      <Section icon="🗂" title="Shop Categories" description="Categories sellers pick when registering a shop. Used for filtering.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {categories.length === 0
            ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0' }}>No categories yet.</div>
            : categories.map(cat => <CategoryItem key={cat.category_id} cat={cat} onEdit={c => setCatModal(c)} onDelete={handleCatDelete} />)}
        </div>
        <button onClick={() => setCatModal({})}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          + Add Category
        </button>
      </Section>

      {/* ── 4b. CREATOR CATEGORIES ── */}
      <Section icon="🎥" title="Creator Categories" description="Categories creators pick for their content (e.g. Fashion, Comedy, Music). Shown in the go-live picker and the admin Creators page.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {creatorCategories.length === 0
            ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0' }}>No creator categories yet.</div>
            : creatorCategories.map(cat => <CreatorCategoryItem key={cat.category_id} cat={cat} onEdit={c => setCreatorCatModal(c)} onDelete={handleCreatorCatDelete} />)}
        </div>
        <button onClick={() => setCreatorCatModal({})}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          + Add Creator Category
        </button>
      </Section>

      {/* ── 4c. VIDEO CATEGORIES ── */}
      <Section icon="🎬" title="Video Categories" description="Categories creators pick when uploading long-form videos (e.g. Movie, Documentary). Shown in the upload form and browse chips.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 14 }}>
          {videoCategories.length === 0
            ? <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '24px 0' }}>No video categories yet.</div>
            : videoCategories.map(cat => <VideoCategoryItem key={cat.category_id} cat={cat} onEdit={c => setVideoCatModal(c)} onDelete={handleVideoCatDelete} />)}
        </div>
        <button onClick={() => setVideoCatModal({})}
          style={{ height: 38, padding: '0 16px', borderRadius: 8, border: '1px dashed #e8440a', background: 'transparent', color: '#e8440a', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
          + Add Video Category
        </button>
      </Section>

      {/* ── 5. PLATFORM LIMITS ── */}
      <Section icon="📏" title="Platform Limits" description="Control maximum values to prevent abuse and manage server load">
        <Grid cols={3}>
          {[
            { key: 'max_products_per_shop',  label: 'Max Products / Shop',    hint: 'How many products a seller can list' },
            { key: 'max_reels_per_day',      label: 'Max Clips / Day',        hint: 'Per seller per day' },
            { key: 'max_images_per_product', label: 'Max Images / Product',   hint: 'Product photo limit' },
            { key: 'max_cart_items',         label: 'Max Items in Cart',      hint: 'Per buyer session' },
            { key: 'order_expiry_minutes',   label: 'Order Expiry (mins)',    hint: 'Unpaid orders expire after this' },
            { key: 'low_stock_threshold',    label: 'Low Stock Threshold',    hint: 'Notify seller when stock ≤ this' },
            { key: 'refund_window_hours',    label: 'Refund Window (hours)',   hint: 'Hours after delivery buyer can request a refund' },
          ].map(({ key, label: l, hint: h }) => (
            <div key={key}>
              <label style={label}>{l}</label>
              <input type="number" min="0" value={config[key]}
                onChange={e => set(key, parseInt(e.target.value))} style={input} />
              <p style={hint}>{h}</p>
            </div>
          ))}
        </Grid>
      </Section>

      {/* ── AUTO-CANCEL FAILED ORDERS ── */}
      <Section icon="🚫" title="Auto-Cancel Failed Orders" description="Automatically cancel orders stuck in a failed/unpaid state after a set time">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Auto-Cancel Enabled</div>
              <div style={hint}>Automatically cancel failed / unpaid orders once the threshold is reached</div>
            </div>
            <Toggle checked={!!config.auto_cancel_failed_enabled} onChange={v => set('auto_cancel_failed_enabled', v)} />
          </div>

          {config.auto_cancel_failed_enabled && (
            <Grid cols={2}>
              <div>
                <label style={label}>Threshold (hours)</label>
                <input type="number" min="1" value={config.auto_cancel_failed_hours}
                  onChange={e => set('auto_cancel_failed_hours', parseInt(e.target.value))} style={input} />
                <p style={hint}>Cancel orders that have been in a failed/unpaid state for longer than this — default 48 h</p>
              </div>
              <div>
                <label style={label}>Check Interval (hours)</label>
                <input type="number" min="1" max="24" value={config.auto_cancel_check_interval_hours}
                  onChange={e => set('auto_cancel_check_interval_hours', parseInt(e.target.value))} style={input} />
                <p style={hint}>How often the job runs to look for stale orders — default 1 h (runs every hour)</p>
              </div>
            </Grid>
          )}
        </div>
      </Section>

      {/* ── 6. CONTENT LIMITS ── */}
      <Section icon="🎬" title="Clips & Live Settings" description="Control video content durations and live stream settings">
        <Grid cols={3}>
          <div>
            <label style={label}>Max Clip Duration (seconds)</label>
            <input type="number" min="1" value={config.reel_max_duration_secs}
              onChange={e => set('reel_max_duration_secs', parseInt(e.target.value))} style={input} />
            <p style={hint}>Currently {config.reel_max_duration_secs}s ({(config.reel_max_duration_secs/60).toFixed(1)} min)</p>
          </div>
          <div>
            <label style={label}>Max Live Stream Duration (mins)</label>
            <input type="number" min="1" value={config.live_max_duration_mins}
              onChange={e => set('live_max_duration_mins', parseInt(e.target.value))} style={input} />
            <p style={hint}>Auto-end streams after this duration</p>
          </div>
          <div>
            <label style={label}>Quick Live Confirm (secs)</label>
            <input type="number" min="5" max="300" value={config.quick_live_confirm_secs}
              onChange={e => set('quick_live_confirm_secs', parseInt(e.target.value))} style={input} />
            <p style={hint}>Seconds seller has to confirm a live stream order before it auto-cancels (currently {config.quick_live_confirm_secs}s)</p>
          </div>
        </Grid>
      </Section>

      {/* ── 7. REGISTRATIONS ── */}
      <Section icon="🔐" title="Registrations" description="Control who can join the platform right now">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {[
            { key: 'allow_seller_registration', label: 'Seller Registration',  sub: 'Allow new shops to register' },
            { key: 'allow_driver_registration', label: 'Driver Registration',  sub: 'Allow new drivers to register' },
            { key: 'notify_new_order',          label: 'Order Notifications',  sub: 'Push notify admins on new orders' },
            { key: 'notify_low_stock',          label: 'Low Stock Alerts',     sub: 'Notify sellers when stock is low' },
          ].map(({ key, label: l, sub }) => (
            <div key={key} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>{l}</div>
                <div style={hint}>{sub}</div>
              </div>
              <Toggle checked={!!config[key]} onChange={v => set(key, v)} />
            </div>
          ))}
        </div>
      </Section>

      {/* ── 8. SUPPORT INFO ── */}
      <Section icon="🛟" title="Support & App Info" description="Contact info shown to users in the app">
        <Grid cols={2}>
          <div>
            <label style={label}>App Name</label>
            <input value={config.app_name} onChange={e => set('app_name', e.target.value)} style={input} />
          </div>
          <div>
            <label style={label}>App Version</label>
            <input value={config.app_version} onChange={e => set('app_version', e.target.value)} style={input} />
            <p style={hint}>Display-only, for your reference</p>
          </div>
          <div>
            <label style={label}>Support Phone</label>
            <input value={config.support_phone} onChange={e => set('support_phone', e.target.value)} placeholder="+250 78X XXX XXX" style={input} />
          </div>
          <div>
            <label style={label}>Support Email</label>
            <input type="email" value={config.support_email} onChange={e => set('support_email', e.target.value)} placeholder="support@kudos.rw" style={input} />
          </div>
        </Grid>
      </Section>


      {/* ── TIPS ── */}
      <Section icon="💝" title="Live Tips" description="Configure tip buttons buyers tap during live streams — each button has one emoji and one amount">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>

          {/* Toggle */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
            <div>
              <div style={{ fontSize: 13, fontWeight: 700 }}>Tips Enabled</div>
              <div style={hint}>Allow buyers to send tips during live streams</div>
            </div>
            <Toggle checked={!!config.tips_enabled} onChange={v => set('tips_enabled', v)} />
          </div>

          {config.tips_enabled && (
            <>
              {/* Existing tip pairs */}
              <div>
                <label style={label}>Tip Buttons ({(config.tip_amounts || []).length})</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 12 }}>
                  {(config.tip_amounts || []).map((amt, i) => {
                    const emoji = (config.tip_emojis || [])[i] || '💝'
                    return (
                      <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 14px', background: '#fafaf8', border: '1px solid #e9e9e4', borderRadius: 9 }}>
                        {/* Emoji badge */}
                        <div style={{ width: 38, height: 38, borderRadius: 10, background: '#fff', border: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>
                          {emoji}
                        </div>
                        {/* Amount */}
                        <div style={{ flex: 1 }}>
                          <div style={{ fontSize: 14, fontWeight: 800, fontFamily: "'JetBrains Mono',monospace" }}>{Number(amt).toLocaleString()} RWF</div>
                          <div style={{ fontSize: 10, color: '#9a9a94', marginTop: 1 }}>Button {i + 1}</div>
                        </div>
                        {/* Delete */}
                        <button type="button"
                          onClick={() => {
                            const newAmounts = (config.tip_amounts || []).filter((_, idx) => idx !== i)
                            const newEmojis  = (config.tip_emojis  || []).filter((_, idx) => idx !== i)
                            set('tip_amounts', newAmounts)
                            set('tip_emojis',  newEmojis)
                          }}
                          style={{ padding: '4px 10px', borderRadius: 6, border: 'none', background: '#fef2f2', color: '#b91c1c', fontSize: 11, fontWeight: 700, cursor: 'pointer' }}>
                          Remove
                        </button>
                      </div>
                    )
                  })}
                  {(config.tip_amounts || []).length === 0 && (
                    <div style={{ textAlign: 'center', color: '#9a9a94', fontSize: 13, padding: '16px 0', border: '1px dashed #e9e9e4', borderRadius: 8 }}>
                      No tip buttons yet. Add one below.
                    </div>
                  )}
                </div>
              </div>

              {/* Add new tip — emoji + amount together */}
              <TipAdder
                onAdd={(emoji, amount) => {
                  set('tip_amounts', [...(config.tip_amounts || []), amount])
                  set('tip_emojis',  [...(config.tip_emojis  || []), emoji])
                }}
              />

              {/* In-app preview */}
              {(config.tip_amounts || []).length > 0 && (
                <div>
                  <label style={label}>In-App Preview</label>
                  <div style={{ background: '#111317', borderRadius: 12, padding: '16px', display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    {(config.tip_amounts || []).map((amt, i) => {
                      const emoji = (config.tip_emojis || [])[i] || '💝'
                      return (
                        <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.15)', borderRadius: 20, padding: '7px 14px', color: '#fff', fontSize: 13, fontWeight: 700, backdropFilter: 'blur(8px)' }}>
                          <span style={{ fontSize: 18 }}>{emoji}</span>
                          <span>{Number(amt).toLocaleString()}</span>
                        </div>
                      )
                    })}
                  </div>
                  <p style={hint}>This is how tip buttons appear during a live stream.</p>
                </div>
              )}
            </>
          )}
        </div>
      </Section>

      {/* ── 9. SELLER TIERS ── */}
      <Section icon="🏆" title="Seller Tier Thresholds" description="Sellers auto-qualify for a tier when they meet either the revenue OR orders threshold. Bronze is the default for all new sellers.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {hasTierErrors && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b91c1c', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
              <div>
                <b>Tier discount conflict:</b> Commission discounts must be strictly less than the platform commission rate ({(basePct*100).toFixed(1)}%) and in ascending order (Silver ≤ Gold ≤ Platinum). Fix the errors below before saving.
              </div>
            </div>
          )}
          {[
            { id: 'silver',   emoji: '🥈', label: 'Silver',   color: '#475569', bg: '#f1f5f9', border: '#e2e8f0' },
            { id: 'gold',     emoji: '🥇', label: 'Gold',     color: '#713f12', bg: '#fefce8', border: '#fde047' },
            { id: 'platinum', emoji: '💎', label: 'Platinum', color: '#3730a3', bg: '#eef2ff', border: '#c7d2fe' },
          ].map(t => (
            <div key={t.id} style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>{t.emoji}</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.color }}>{t.label} Tier</div>
                <div style={{ fontSize: 11, color: t.color, opacity: 0.7, fontFamily: "'JetBrains Mono',monospace" }}>— either threshold qualifies</div>
              </div>
              <Grid cols={3}>
                <div>
                  <label style={label}>Min Revenue (RWF)</label>
                  <input type="number" min="0" value={config[`tier_${t.id}_min_revenue`]}
                    onChange={e => set(`tier_${t.id}_min_revenue`, parseInt(e.target.value))} style={input} />
                  <p style={hint}>Lifetime seller revenue</p>
                </div>
                <div>
                  <label style={label}>Min Orders</label>
                  <input type="number" min="0" value={config[`tier_${t.id}_min_orders`]}
                    onChange={e => set(`tier_${t.id}_min_orders`, parseInt(e.target.value))} style={input} />
                  <p style={hint}>Total completed orders</p>
                </div>
                <div>
                  <label style={label}>Commission Discount (decimal)</label>
                  <input type="number" min="0" max={Math.max(0, basePct - 0.001).toFixed(3)} step="0.01"
                    value={config[`tier_${t.id}_commission_discount`]}
                    onChange={e => set(`tier_${t.id}_commission_discount`, parseFloat(e.target.value))}
                    style={{ ...input, borderColor: tierDiscErrors[t.id] ? '#ef4444' : undefined, outline: tierDiscErrors[t.id] ? '1px solid #ef4444' : undefined }} />
                  {tierDiscErrors[t.id]
                    ? <p style={{ ...hint, color: '#b91c1c', fontWeight: 600 }}>⚠ {tierDiscErrors[t.id]}</p>
                    : <p style={hint}>Must be &lt; platform commission ({(basePct*100).toFixed(1)}%). e.g. 0.02 = 2pp off</p>
                  }
                </div>
              </Grid>
            </div>
          ))}
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#92400e' }}>
            🥉 <b>Bronze</b> is the default tier for all sellers — no minimum required. Commission discount: 0%.
          </div>
        </div>
      </Section>

      {/* ── 9b. CREATOR TIERS ── */}
      <Section icon="📺" title="Creator Tier Thresholds" description="Creators auto-qualify for a tier based on cumulative live-stream views (peak viewers summed across all their streams). Bronze is the default for all new creators.">
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {hasCreatorTierErrors && (
            <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: '#b91c1c', display: 'flex', gap: 8, alignItems: 'flex-start' }}>
              <span style={{ fontSize: 16, flexShrink: 0 }}>⚠</span>
              <div>
                <b>Tier discount conflict:</b> Commission discounts must be strictly less than the tip commission rate ({(tipBasePct*100).toFixed(1)}%) and in ascending order (Silver ≤ Gold ≤ Platinum). Fix the errors below before saving.
              </div>
            </div>
          )}
          {[
            { id: 'silver',   emoji: '🥈', label: 'Silver',   color: '#475569', bg: '#f1f5f9', border: '#e2e8f0' },
            { id: 'gold',     emoji: '🥇', label: 'Gold',     color: '#713f12', bg: '#fefce8', border: '#fde047' },
            { id: 'platinum', emoji: '💎', label: 'Platinum', color: '#3730a3', bg: '#eef2ff', border: '#c7d2fe' },
          ].map(t => (
            <div key={t.id} style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 10, padding: '14px 18px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 14 }}>
                <span style={{ fontSize: 20 }}>{t.emoji}</span>
                <div style={{ fontSize: 13, fontWeight: 800, color: t.color }}>{t.label} Tier</div>
              </div>
              <Grid cols={2}>
                <div>
                  <label style={label}>Min Cumulative Views</label>
                  <input type="number" min="0" value={config[`creator_tier_${t.id}_min_views`]}
                    onChange={e => set(`creator_tier_${t.id}_min_views`, parseInt(e.target.value))} style={input} />
                  <p style={hint}>Sum of peak viewers across all live streams</p>
                </div>
                <div>
                  <label style={label}>Commission Discount (decimal)</label>
                  <input type="number" min="0" max={Math.max(0, tipBasePct - 0.001).toFixed(3)} step="0.01"
                    value={config[`creator_tier_${t.id}_commission_discount`]}
                    onChange={e => set(`creator_tier_${t.id}_commission_discount`, parseFloat(e.target.value))}
                    style={{ ...input, borderColor: creatorTierDiscErrors[t.id] ? '#ef4444' : undefined, outline: creatorTierDiscErrors[t.id] ? '1px solid #ef4444' : undefined }} />
                  {creatorTierDiscErrors[t.id]
                    ? <p style={{ ...hint, color: '#b91c1c', fontWeight: 600 }}>⚠ {creatorTierDiscErrors[t.id]}</p>
                    : <p style={hint}>Must be &lt; tip commission ({(tipBasePct*100).toFixed(1)}%). e.g. 0.02 = 2pp off</p>
                  }
                </div>
              </Grid>
            </div>
          ))}
          <div style={{ background: '#fef3c7', border: '1px solid #fde68a', borderRadius: 10, padding: '12px 16px', fontSize: 12, color: '#92400e' }}>
            🥉 <b>Bronze</b> is the default tier for all creators — no minimum required. Commission discount: 0%. Admins can also set a manual override per creator from the Creators page.
          </div>
        </div>
      </Section>

      {/* ── 10. MAINTENANCE ── */}
      <Section icon="🚧" title="Maintenance Mode" description="Take the app offline for users without touching code" danger={config.maintenance_mode}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 20 }}>
          <div style={{ flex: 1 }}>
            <label style={label}>Maintenance Message</label>
            <textarea value={config.maintenance_message}
              onChange={e => set('maintenance_message', e.target.value)}
              style={{ ...input, height: 80, padding: '10px 12px', resize: 'vertical' }} />
            <p style={hint}>Shown to users when maintenance mode is ON</p>
          </div>
          <div style={{ paddingTop: 24 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '12px 16px', background: config.maintenance_mode ? '#fef2f2' : '#fafaf8', borderRadius: 8, border: `1px solid ${config.maintenance_mode ? '#fecaca' : '#e9e9e4'}` }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 800, color: config.maintenance_mode ? '#b91c1c' : '#111317' }}>
                  {config.maintenance_mode ? '🔴 App is OFFLINE' : '🟢 App is LIVE'}
                </div>
                <div style={hint}>Toggle to enable/disable</div>
              </div>
              <Toggle checked={!!config.maintenance_mode} onChange={v => set('maintenance_mode', v)} />
            </div>
          </div>
        </div>
      </Section>

      {/* Save button (bottom) */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={handleSaveConfig} disabled={saving || hasTierErrors || hasCreatorTierErrors}
          style={{ height: 44, padding: '0 28px', borderRadius: 9, border: 'none',
            background: (hasTierErrors || hasCreatorTierErrors) ? '#9ca3af' : saved ? '#15803d' : saving ? '#f0c4b4' : '#e8440a',
            color: '#fff', fontSize: 14, fontWeight: 700,
            cursor: (saving || hasTierErrors || hasCreatorTierErrors) ? 'not-allowed' : 'pointer',
            fontFamily: "'Plus Jakarta Sans',sans-serif",
            boxShadow: (hasTierErrors || hasCreatorTierErrors) ? 'none' : '0 2px 12px rgba(232,68,10,0.25)' }}>
          {(hasTierErrors || hasCreatorTierErrors) ? '⚠ Fix Tier Discount Errors' : saved ? '✓ All Settings Saved!' : saving ? 'Saving…' : 'Save All Settings'}
        </button>
      </div>

      {/* Category Modal */}
      {catModal !== null && (
        <CategoryModal cat={Object.keys(catModal).length ? catModal : null} onClose={() => setCatModal(null)} onSaved={handleCatSaved} />
      )}

      {/* Creator Category Modal */}
      {creatorCatModal !== null && (
        <CreatorCategoryModal cat={Object.keys(creatorCatModal).length ? creatorCatModal : null} onClose={() => setCreatorCatModal(null)} onSaved={handleCreatorCatSaved} />
      )}

      {/* Video Category Modal */}
      {videoCatModal !== null && (
        <VideoCategoryModal cat={Object.keys(videoCatModal).length ? videoCatModal : null} onClose={() => setVideoCatModal(null)} onSaved={handleVideoCatSaved} />
      )}

      {/* Zone Modal */}
      {zoneModal !== null && (
        <>
          <div onClick={() => setZoneModal(null)} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.22)', zIndex: 300 }} />
          <div style={{ position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)', width: 420, background: '#fff', borderRadius: 14, zIndex: 301, boxShadow: '0 20px 60px rgba(0,0,0,0.15)', animation: 'popIn 0.2s ease' }}>
            <div style={{ padding: '18px 22px', borderBottom: '1px solid #e9e9e4', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 15, fontWeight: 800 }}>{zoneModal.zone_id ? 'Edit Zone' : 'New Delivery Zone'}</div>
              <div onClick={() => setZoneModal(null)} style={{ cursor: 'pointer', fontSize: 20, color: '#9a9a94' }}>×</div>
            </div>
            <form onSubmit={handleZoneSave}>
              <div style={{ padding: '18px 22px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* Zone area — Google Autocomplete */}
                <div>
                  <label style={label}>Zone Area *</label>
                  <PlaceAutocomplete
                    value={zoneForm.zone_name}
                    onChange={v => setZoneForm(f => ({ ...f, zone_name: v }))}
                    onPlaceSelect={p => setZoneForm(f => ({
                      ...f,
                      zone_name        : p.zone_name || f.zone_name,
                      city             : p.city,
                      district         : p.district,
                      area             : p.area,
                      lat              : p.lat,
                      lng              : p.lng,
                      formatted_address: p.formatted_address,
                    }))}
                    placeholder="Search city, district or area…"
                    style={{ ...input }}
                  />
                  {zoneForm.formatted_address && (
                    <p style={{ ...hint, color: '#16a34a', marginTop: 5 }}>
                      ✓ {zoneForm.formatted_address}
                    </p>
                  )}
                </div>

                {/* Auto-filled location details */}
                {(zoneForm.city || zoneForm.district || zoneForm.area) && (
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
                    {[
                      { lbl: 'City',     val: zoneForm.city     },
                      { lbl: 'District', val: zoneForm.district },
                      { lbl: 'Area',     val: zoneForm.area     },
                    ].map(({ lbl, val }) => (
                      <div key={lbl} style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 7, padding: '7px 10px' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#16a34a', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 2 }}>{lbl}</div>
                        <div style={{ fontSize: 12, fontWeight: 700, color: '#111317' }}>{val || '—'}</div>
                      </div>
                    ))}
                  </div>
                )}

                {/* Coordinates (read-only) */}
                {zoneForm.lat && (
                  <div style={{ display: 'flex', gap: 8 }}>
                    {[['Lat', zoneForm.lat], ['Lng', zoneForm.lng]].map(([lbl, val]) => (
                      <div key={lbl} style={{ flex: 1, background: '#f9f9f7', border: '1px solid #e9e9e4', borderRadius: 7, padding: '7px 10px' }}>
                        <div style={{ fontSize: 9, fontWeight: 700, color: '#9a9a94', textTransform: 'uppercase', letterSpacing: '0.08em', fontFamily: "'JetBrains Mono',monospace", marginBottom: 2 }}>{lbl}</div>
                        <div style={{ fontSize: 11, fontWeight: 600, color: '#374151', fontFamily: "'JetBrains Mono',monospace" }}>{Number(val).toFixed(6)}</div>
                      </div>
                    ))}
                  </div>
                )}

                <div>
                  <label style={label}>Description</label>
                  <input value={zoneForm.description} onChange={e => setZoneForm(f => ({ ...f, description: e.target.value }))} placeholder="e.g. CBD & Nyarugenge" style={input} />
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                  <div>
                    <label style={label}>Base Fee (RWF) *</label>
                    <input required type="number" min="0" value={zoneForm.fee} onChange={e => setZoneForm(f => ({ ...f, fee: e.target.value }))} placeholder="1000" style={input} />
                  </div>
                  <div>
                    <label style={label}>Per KM Fee (RWF)</label>
                    <input type="number" min="0" value={zoneForm.per_km_fee} onChange={e => setZoneForm(f => ({ ...f, per_km_fee: e.target.value }))} placeholder="0 = fixed" style={input} />
                  </div>
                </div>

                <div>
                  <label style={label}>Delivery Time</label>
                  <select value={zoneForm.delivery_time} onChange={e => setZoneForm(f => ({ ...f, delivery_time: e.target.value }))}
                    style={{ ...input, cursor: 'pointer' }}>
                    <option value="same_day">Same Day</option>
                    <option value="next_day">Next Day</option>
                    <option value="2-3_days">2–3 Days</option>
                    <option value="3-5_days">3–5 Days</option>
                    <option value="1_week">1 Week</option>
                  </select>
                </div>

                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '11px 14px', background: '#fafaf8', borderRadius: 8, border: '1px solid #e9e9e4' }}>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700 }}>Zone Available</div>
                    <div style={{ fontSize: 11, color: '#9a9a94', marginTop: 1 }}>Buyers can select this zone at checkout</div>
                  </div>
                  <div onClick={() => setZoneForm(f => ({ ...f, available: !f.available }))}
                    style={{ width: 44, height: 24, borderRadius: 99, background: zoneForm.available ? '#16a34a' : '#e9e9e4', position: 'relative', cursor: 'pointer', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 3, left: zoneForm.available ? 22 : 3, width: 18, height: 18, borderRadius: '50%', background: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.2)', transition: 'left 0.2s' }} />
                  </div>
                </div>
              </div>
              <div style={{ padding: '12px 22px 20px', display: 'flex', gap: 10 }}>
                <button type="button" onClick={() => setZoneModal(null)} style={{ flex: 1, height: 42, borderRadius: 8, border: '1px solid #e9e9e4', background: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>Cancel</button>
                <button type="submit" disabled={zoneLoading} style={{ flex: 1, height: 42, borderRadius: 8, border: 'none', background: zoneLoading ? '#f0c4b4' : '#e8440a', color: '#fff', fontSize: 13, fontWeight: 700, cursor: zoneLoading ? 'not-allowed' : 'pointer', fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                  {zoneLoading ? 'Saving…' : zoneModal.zone_id ? 'Save Changes' : 'Create Zone'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  )
}